/**
 * PublisherGeographyMapCard — a compact world map for the Overview geography summary.
 *
 * The Overview already has the geography data in hand, so this component only renders it. No API
 * calls, no store access, and no invented countries or stream counts. It now delegates the world
 * geometry to a lightweight bundled SVG map library, then applies the Publisher card styling and
 * detail readout around it.
 */
import { useMemo, useState, type CSSProperties, type FocusEvent, type MouseEvent } from 'react';
import WorldMap, { type DataItem } from 'react-svg-worldmap';

import { c } from '../../styles/theme';
import type { PublisherAnalyticsCountryRow } from '../../types/domain';

export interface PublisherGeographyMapCardProps {
  countries: readonly PublisherAnalyticsCountryRow[];
}

interface HoveredCountry {
  countryCode: string;
  countryName: string;
  streams: string;
  share: string;
}

const cardStyle: CSSProperties = {
  border: `1px solid ${c.border}`,
  borderRadius: 10,
  padding: '12px 14px',
  background: c.surface1,
  display: 'grid',
  gap: 10,
};

function parsePercent(value: string): number {
  const parsed = Number.parseFloat(value.replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapFillColor(sharePercent: number, maxShare: number): string {
  if (sharePercent <= 0 || maxShare <= 0) return c.surface2;
  const strength = Math.max(0, Math.min(1, Math.sqrt(sharePercent / maxShare)));
  const alpha = 0.16 + strength * 0.52;
  return `rgba(242,169,60,${alpha.toFixed(3)})`;
}

function mapStrokeColor(sharePercent: number): string {
  return sharePercent > 0 ? c.accentBorder : c.border;
}

function hoveredCountryFromTarget(
  target: EventTarget | null,
  countriesByLabel: ReadonlyMap<string, HoveredCountry>
): HoveredCountry | null {
  if (!(target instanceof Element)) return null;
  const labelledRegion = target.closest('a[aria-label], path[aria-label]');
  if (!labelledRegion) return null;
  const label = labelledRegion.getAttribute('aria-label');
  if (!label) return null;
  return countriesByLabel.get(label) ?? null;
}

export default function PublisherGeographyMapCard({ countries }: PublisherGeographyMapCardProps) {
  const [hoveredCountry, setHoveredCountry] = useState<HoveredCountry | null>(null);

  const mapCountries = useMemo(() => {
    return countries.map((country) => ({
      ...country,
      normalizedCode: country.countryCode.trim().toUpperCase(),
      sharePercent: parsePercent(country.share),
    }));
  }, [countries]);

  const countriesByCode = useMemo(
    () =>
      new Map(
        mapCountries.map((country) => [
          country.normalizedCode,
          {
            countryCode: country.normalizedCode,
            countryName: country.countryName,
            streams: country.streams,
            share: country.share,
          },
        ])
      ),
    [mapCountries]
  );

  const countriesByLabel = useMemo(
    () => new Map(mapCountries.map((country) => [country.countryName, countriesByCode.get(country.normalizedCode)!])),
    [countriesByCode, mapCountries]
  );

  const mapData = useMemo<DataItem<number>[]>(
    () =>
      mapCountries.map((country) => ({
        country: country.normalizedCode.toLowerCase() as DataItem<number>['country'],
        value: country.sharePercent,
      })),
    [mapCountries]
  );

  const maxShare = useMemo(
    () => mapCountries.reduce((highest, country) => Math.max(highest, country.sharePercent), 0),
    [mapCountries]
  );

  function handleMapMouseOver(event: MouseEvent<HTMLDivElement>) {
    const nextCountry = hoveredCountryFromTarget(event.target, countriesByLabel);
    if (nextCountry) setHoveredCountry(nextCountry);
  }

  function handleMapMouseOut(event: MouseEvent<HTMLDivElement>) {
    const leavingCountry = hoveredCountryFromTarget(event.target, countriesByLabel);
    if (!leavingCountry) return;
    const enteringCountry = hoveredCountryFromTarget(event.relatedTarget, countriesByLabel);
    if (enteringCountry) {
      setHoveredCountry(enteringCountry);
      return;
    }
    setHoveredCountry((current) => (current?.countryCode === leavingCountry.countryCode ? null : current));
  }

  function handleMapFocus(event: FocusEvent<HTMLDivElement>) {
    const nextCountry = hoveredCountryFromTarget(event.target, countriesByLabel);
    if (nextCountry) setHoveredCountry(nextCountry);
  }

  function handleMapBlur(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setHoveredCountry(null);
  }

  return (
    <section style={cardStyle} aria-label="Geography streams map">
      <style>
        {`
          .publisher-geography-map-card__map .worldmap__figure-container {
            margin: 0;
          }

          .publisher-geography-map-card__map svg {
            display: block;
            max-width: 100%;
            height: auto;
          }
        `}
      </style>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <p style={{ margin: '0 0 4px', fontSize: 11, color: c.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
            Geography Streams
          </p>
          <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 14, color: c.text1 }}>
            World map by stream share
          </p>
        </div>
        <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>Shaded by share of all-time streams.</p>
      </div>

      <div
        className="publisher-geography-map-card__map"
        style={{ position: 'relative' }}
        onMouseOver={handleMapMouseOver}
        onMouseOut={handleMapMouseOut}
        onFocus={handleMapFocus}
        onBlur={handleMapBlur}
      >
        <WorldMap
          data={mapData}
          size="responsive"
          color={c.accent}
          backgroundColor="transparent"
          tooltipBgColor="transparent"
          tooltipTextColor="transparent"
          borderColor={c.border}
          strokeOpacity={1}
          tooltipTextFunction={(context) => {
            const country = countriesByCode.get(context.countryCode.toUpperCase());
            return country ? `${country.countryName}: ${country.streams} streams` : context.countryName;
          }}
          hrefFunction={(context) => {
            const country = countriesByCode.get(context.countryCode.toUpperCase());
            if (!country) return undefined;
            return {
              href: '#',
              role: 'button',
              onClick: (event) => event.preventDefault(),
            };
          }}
          styleFunction={(context) => {
            const sharePercent = typeof context.countryValue === 'number' ? context.countryValue : 0;
            const country = countriesByCode.get(context.countryCode.toUpperCase());
            return {
              fill: mapFillColor(sharePercent, maxShare),
              stroke: mapStrokeColor(sharePercent),
              strokeWidth: country ? 1.45 : 0.8,
              cursor: country ? 'pointer' : 'default',
              outline: 'none',
            };
          }}
        />

        {hoveredCountry ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              maxWidth: 220,
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              padding: '10px 12px',
              background: c.surface2,
              boxShadow: `0 8px 24px ${c.bg}`,
              display: 'grid',
              gap: 4,
              pointerEvents: 'none',
            }}
          >
            <p style={{ margin: 0, fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: 13, color: c.text1 }}>
              {hoveredCountry.countryName}
            </p>
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
              {hoveredCountry.streams} streams
            </p>
            <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>{hoveredCountry.share} of all-time streams</p>
          </div>
        ) : null}
      </div>

      <p style={{ margin: 0, fontSize: 11, color: c.text2, lineHeight: 1.55 }}>
        Hover or focus a highlighted country to inspect its reported stream count.
      </p>
    </section>
  );
}
